/**
 * Pure state for the "How Ako is thinking" trace. Only learner-safe step
 * labels ever enter this state (status labels / short `reasoning` summaries
 * from the server); raw model thinking is dropped upstream in
 * normalizeStreamEvent and never reaches here.
 */
export type TracePhase = 'idle' | 'thinking' | 'tool' | 'writing' | 'done' | 'stopped' | 'error'
export type StepStatus = 'active' | 'done' | 'stopped'

export interface TraceStep {
  id: number
  label: string
  status: StepStatus
}

export interface TraceState {
  phase: TracePhase
  steps: TraceStep[]
  startedAt: number | null
  /** Set when the first answer token arrives (thinking ends). */
  thinkingEndedAt: number | null
  endedAt: number | null
  nextId: number
}

export type TraceAction =
  | { type: 'begin'; at: number }
  | { type: 'status'; at: number; phase: 'thinking' | 'tool' | 'writing'; label?: string }
  | { type: 'reasoning'; at: number; text: string }
  | { type: 'delta'; at: number }
  | { type: 'done'; at: number }
  | { type: 'abort'; at: number }
  | { type: 'error'; at: number }
  | { type: 'reset' }

export const MAX_STEPS = 8

export const initialTrace: TraceState = {
  phase: 'idle',
  steps: [],
  startedAt: null,
  thinkingEndedAt: null,
  endedAt: null,
  nextId: 1,
}

const settle = (steps: TraceStep[], to: StepStatus): TraceStep[] =>
  steps.map((s) => (s.status === 'active' ? { ...s, status: to } : s))

function pushStep(state: TraceState, label: string): TraceState {
  const last = state.steps[state.steps.length - 1]
  if (last && last.label === label && last.status === 'active') return state
  const settled = settle(state.steps, 'done')
  const next = [...settled, { id: state.nextId, label, status: 'active' as const }]
  return { ...state, steps: next.slice(-MAX_STEPS), nextId: state.nextId + 1 }
}

export function traceReducer(state: TraceState, action: TraceAction): TraceState {
  switch (action.type) {
    case 'begin':
      return { ...initialTrace, phase: 'thinking', startedAt: action.at }
    case 'reset':
      return initialTrace
    case 'reasoning': {
      if (state.phase === 'idle' || state.phase === 'done' || state.phase === 'stopped') return state
      if (state.thinkingEndedAt !== null) return state
      return pushStep(state, action.text)
    }
    case 'status': {
      if (state.phase === 'idle' || state.phase === 'done' || state.phase === 'stopped') return state
      if (action.phase === 'writing') {
        return {
          ...state,
          phase: 'writing',
          steps: settle(state.steps, 'done'),
          thinkingEndedAt: state.thinkingEndedAt ?? action.at,
        }
      }
      const withPhase = { ...state, phase: action.phase }
      return action.label && state.thinkingEndedAt === null
        ? pushStep(withPhase, action.label)
        : withPhase
    }
    case 'delta': {
      if (state.phase === 'idle' || state.phase === 'done' || state.phase === 'stopped') return state
      if (state.thinkingEndedAt !== null) return state
      return {
        ...state,
        phase: 'writing',
        steps: settle(state.steps, 'done'),
        thinkingEndedAt: action.at,
      }
    }
    case 'done':
      if (state.phase === 'idle') return state
      return {
        ...state,
        phase: 'done',
        steps: settle(state.steps, 'done'),
        thinkingEndedAt: state.thinkingEndedAt ?? action.at,
        endedAt: action.at,
      }
    case 'abort':
      if (state.phase === 'idle' || state.phase === 'done') return state
      return { ...state, phase: 'stopped', steps: settle(state.steps, 'stopped'), endedAt: action.at }
    case 'error':
      if (state.phase === 'idle' || state.phase === 'done') return state
      return { ...state, phase: 'error', steps: settle(state.steps, 'stopped'), endedAt: action.at }
    default:
      return state
  }
}

export const isTraceLive = (s: TraceState): boolean =>
  s.phase === 'thinking' || s.phase === 'tool' || s.phase === 'writing'

/** Time spent thinking (before the first answer token), in ms. */
export function thinkingDurationMs(s: TraceState, now: number): number {
  if (s.startedAt === null) return 0
  return Math.max(0, (s.thinkingEndedAt ?? s.endedAt ?? now) - s.startedAt)
}

export function formatDuration(ms: number): string {
  const total = Math.round(ms / 1000)
  if (total < 60) return `${total}s`
  const m = Math.floor(total / 60)
  const sec = String(total % 60).padStart(2, '0')
  return `${m}m ${sec}s`
}
